import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';
import { BandLimitedOscillator, type HarmonicWavetable, type Waveform } from './oscillator.js';

const waveforms: readonly Waveform[] = ['sine', 'triangle', 'saw', 'pulse', 'wavetable'];
const defaultWavetable: HarmonicWavetable = {
  real: [0, 0, 0, 0, 0, 0, 0, 0, 0],
  imag: [0, 1, 1 / 2, 1 / 3, 1 / 4, 1 / 5, 1 / 6, 1 / 7, 1 / 8],
};

class OscillatorModuleInstance implements DspModuleInstance {
  private readonly oscillator: BandLimitedOscillator;
  private readonly maxBlockSize: number;

  constructor(sampleRate: number, maxBlockSize: number) {
    this.maxBlockSize = maxBlockSize;
    this.oscillator = new BandLimitedOscillator({
      sampleRate,
      waveform: 'saw',
      frequency: 220,
      wavetable: defaultWavetable,
    });
  }

  reset(): void { this.oscillator.reset(); }

  setParameter(id: string, value: number): void {
    if (id === 'frequency') return this.oscillator.setFrequency(value);
    if (id === 'pulse-width') return this.oscillator.setPulseWidth(value);
    if (id === 'phase-distortion') return this.oscillator.setPhaseDistortion(value);
    if (id === 'reverse-phase') {
      if (value !== 0 && value !== 1) throw new RangeError('reverse-phase must be 0 or 1');
      return this.oscillator.setReversePhase(value === 1);
    }
    if (id === 'invert-polarity') {
      if (value !== 0 && value !== 1) throw new RangeError('invert-polarity must be 0 or 1');
      return this.oscillator.setInvertPolarity(value === 1);
    }
    if (id === 'waveform') {
      if (!Number.isInteger(value) || value < 0 || value >= waveforms.length) throw new RangeError('waveform must be an integer in [0, 4]');
      return this.oscillator.setWaveform(waveforms[value] as Waveform);
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const output = block.outputs.out;
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.oscillator.processInto(output, 0, block.frames);
  }
}

export const mod001 = defineDspModule({
  descriptor: {
    id: 'mir.mod-001',
    name: 'Band-limited oscillator',
    version: '0.1.0',
    inputs: [],
    outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'frequency', defaultValue: 220, minValue: 0, maxValue: 20_000, unit: 'Hz' },
      { id: 'waveform', defaultValue: 2, minValue: 0, maxValue: 4, unit: 'enum' },
      { id: 'pulse-width', defaultValue: 0.5, minValue: 0.01, maxValue: 0.99, unit: 'ratio' },
      { id: 'phase-distortion', defaultValue: 0, minValue: -0.95, maxValue: 0.95, unit: 'ratio' },
      { id: 'reverse-phase', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'boolean' },
      { id: 'invert-polarity', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'boolean' },
    ],
  },
  createInstance(context) {
    if (!(context.sampleRate > 0) || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new OscillatorModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});

export { BandLimitedOscillator } from './oscillator.js';
export type { HarmonicWavetable, OscillatorOptions, Waveform } from './oscillator.js';
